<script setup lang="ts">
import {ColorVariantType} from '@toolify/client/src/modules/ui/enum/ColorVariantType'

interface Props {
  isLoading?: boolean
  color?: ColorVariantType
  size?: number
  fillContainer?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  isLoading: false,
  color: ColorVariantType.Primary,
  size: 20,
  fillContainer: false,
})
</script>

<template>
  <div
    class="logo-icon"
    :class="[
      `color-${props.color}`,
      {
        'is-loading': props.isLoading,
        'fill-container': props.fillContainer,
      },
    ]"
  >
    <div class="elements dot dot-top-1">
      <div class="element dot dot-top-1"/>
    </div>
    <div class="elements dot dot-top-2">
      <div class="element dot dot-top-2"/>
    </div>

    <div class="elements dot dot-top-3">
      <div class="element dot dot-top-3"/>
    </div>

    <div class="elements line line-top">
      <div class="element line line-top"/>
    </div>

    <div class="elements line line-bottom">
      <div class="element line line-bottom"/>
    </div>

    <div class="elements dot dot-bottom-1">
      <div class="element dot dot-bottom-1"/>
    </div>

    <div class="elements dot dot-bottom-2">
      <div class="element dot dot-bottom-2"/>
    </div>

    <div class="elements dot dot-bottom-3">
      <div class="element dot dot-bottom-3"/>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../../styles/variables' as variables;



$elementSize: calc(10 / 64 * var(--logo-size));
$elementGap: calc(4.4 / 64 * var(--logo-size));
$animationDuration: 1.75s;

.logo-icon {
  --logo-size: calc(v-bind(props.size) * 1px);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  height: var(--logo-size);
  aspect-ratio: 1/1;

  &.color-primary {
    color: map-get(variables.$colors, primary);
  }

  &.color-error {
    color: map-get(variables.$colors, error);
  }

  &.color-neutral {
    color: map-get(variables.$colors, content-normal);
  }

  &.color-dark {
    color: map-get(variables.$colors, content-dark);
  }

  &.color-dim {
    color: map-get(variables.$colors, content-dim);
  }

  &.color-on-primary {
    color: map-get(variables.$colors, on-primary);
  }

  &.color-darkest {
    color: map-get(variables.$colors, content-darkest);
  }
}

.logo-icon.fill-container {
  container-type: size;
  width: 55%;
  height: auto;
  aspect-ratio: 1 / 1;
  max-height: 100%;
  flex-shrink: 0;
  --logo-size: 100cqmin;
}

@keyframes top-line-movement-animation {
  0%, 35%, 65%, 100% {
    transform: translate(0)
  }
  45%, 55% {
    transform: translate(calc($elementSize + $elementGap), 0);
  }
}

@keyframes bottom-line-movement-animation {
  0%, 35%, 65%, 100% {
    transform: translate(0)
  }
  45%, 55% {
    transform: translate(calc(($elementSize + $elementGap) * -1), 0);
  }
}


@keyframes dot-top-1-movement-animation {
  0%, 35%, 65%, 100% {
    transform: rotate(0);
  }
  45%, 55% {
    transform: rotate(-53deg);
  }
}

@keyframes dot-top-2-movement-animation {
  0%, 35%, 65%, 100% {
    transform: rotate(0);
  }
  45%, 55% {
    transform: rotate(-36.6deg);
  }
}

@keyframes dot-top-3-movement-animation {
  0%, 35%, 65%, 100% {
    transform: rotate(0);
  }
  45%, 55% {
    transform: rotate(-53deg);
  }
}

@keyframes dot-bottom-1-movement-animation {
  0%, 35%, 65%, 100% {
    transform: rotate(0);
  }
  45%, 55% {
    transform: rotate(-53deg)
  }
}

@keyframes dot-bottom-2-movement-animation {
  0%, 35%, 65%, 100% {
    transform: rotate(0);
  }
  45%, 55% {
    transform: rotate(-36.6deg);
  }
}

@keyframes dot-bottom-3-movement-animation {
  0%, 35%, 65%, 100% {
    transform: rotate(0);
  }
  45%, 55% {
    transform: rotate(-53deg);
  }
}

.elements {
  display: grid;
  align-content: center;
  justify-content: center;
  grid-template-columns: repeat(4, $elementSize);
  grid-template-rows: repeat(4, $elementSize);

  gap: $elementGap;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  .logo-icon.is-loading & {
    &.line-top {
      animation: top-line-movement-animation $animationDuration cubic-bezier(0, 0.6, 0.3, 1.2) infinite;
    }

    &.line-bottom {
      animation: bottom-line-movement-animation $animationDuration cubic-bezier(0, 0.6, 0.3, 1.2) infinite;
    }

    &.dot-top-1 {
      animation: dot-top-1-movement-animation $animationDuration cubic-bezier(0, 0.6, 0.3, 1.2) infinite;
    }

    &.dot-top-2 {
      animation: dot-top-2-movement-animation $animationDuration cubic-bezier(0, 0.6, 0.3, 1.2) infinite;
    }

    &.dot-top-3 {
      animation: dot-top-3-movement-animation $animationDuration cubic-bezier(0, 0.6, 0.3, 1.2) infinite;
    }

    &.dot-bottom-1 {
      animation: dot-bottom-1-movement-animation $animationDuration cubic-bezier(0, 0.6, 0.3, 1.2) infinite;
    }

    &.dot-bottom-2 {
      animation: dot-bottom-2-movement-animation $animationDuration cubic-bezier(0, 0.6, 0.3, 1.2) infinite;
    }

    &.dot-bottom-3 {
      animation: dot-bottom-3-movement-animation $animationDuration cubic-bezier(0, 0.6, 0.3, 1.2) infinite;
    }
  }

  .element {
    border-radius: map-get(variables.$radiuses, radius-full);
    background-color: currentColor;

    &.dot-top-1 {
        grid-area: 1 / 2 / 2 / 3;
    }

    &.dot-top-2 {
        grid-area: 1 / 3 / 2 / 4;
    }

    &.dot-top-3 {
        grid-area: 2 / 4 / 3 / 5;
    }

    &.dot-bottom-1 {
        grid-area: 3 / 1 / 4 / 2;
    }

    &.dot-bottom-2 {
        grid-area: 4 / 2 / 5 / 3;
    }

    &.dot-bottom-3 {
        grid-area: 4 / 3 / 5 / 4;
    }

    &.line-top {
        grid-area: 2 / 1 / 3 / 4;
    }

    &.line-bottom {
        grid-area: 3 / 2 / 4 / 5;
    }
  }
}
</style>
